import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';
import {
  AdminSupportQueue,
  AdminSupportStats,
  AdminSupportTicketDetail,
  AdminSupportTicketsResponse,
  SupportReplyData,
  SupportRequestData,
  SupportResponse,
  SupportTicketDetail,
  SupportTicketReply,
  SupportTicketsResponse,
  SupportTicketStatus,
} from '../types/support.types';

function unwrap<T>(payload: T | { data: T }): T {
  return payload && typeof payload === 'object' && 'data' in payload
    ? (payload as { data: T }).data
    : (payload as T);
}

class SupportService {
  private api = axios.create({
    baseURL: APP_CONFIG.api.baseUrl,
    timeout: 60000, // 60s pour upload fichiers + envoi email
  });

  constructor() {
    // Setup automatic token refresh interceptors
    tokenManager.setupInterceptors(this.api);
  }

  async sendRequest(data: SupportRequestData): Promise<SupportResponse> {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('content', data.content);

    // Add documents if provided
    if (data.documents && data.documents.length > 0) {
      data.documents.forEach(file => {
        formData.append('documents', file);
      });
    }

    const response = await this.api.post<
      SupportResponse | { data: SupportResponse }
    >('/users/support', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return unwrap(response.data);
  }

  async getTickets(page = 1, limit = 20): Promise<SupportTicketsResponse> {
    const response = await this.api.get<SupportTicketsResponse>(
      '/support/tickets',
      { params: { page, limit } }
    );

    return response.data;
  }

  async getTicket(uuid: string): Promise<SupportTicketDetail> {
    const response = await this.api.get<
      SupportTicketDetail | { data: SupportTicketDetail }
    >(`/support/tickets/${uuid}`);

    return unwrap(response.data);
  }

  async reply(
    uuid: string,
    data: SupportReplyData
  ): Promise<SupportTicketReply> {
    const response = await this.api.post<
      SupportTicketReply | { data: SupportTicketReply }
    >(`/support/tickets/${uuid}/reply`, data);

    return unwrap(response.data);
  }

  async resolveTicket(uuid: string): Promise<SupportTicketDetail> {
    const response = await this.api.patch<
      SupportTicketDetail | { data: SupportTicketDetail }
    >(`/support/tickets/${uuid}/resolve`);
    return unwrap(response.data);
  }

  async getAdminTickets(
    queue: AdminSupportQueue,
    page = 1,
    limit = 20
  ): Promise<AdminSupportTicketsResponse> {
    const response = await this.api.get<
      AdminSupportTicketsResponse | { data: AdminSupportTicketsResponse }
    >('/admin/support/tickets', { params: { queue, page, limit } });
    return 'pagination' in response.data ? response.data : response.data.data;
  }

  async getAdminStats(): Promise<AdminSupportStats> {
    const response = await this.api.get<
      AdminSupportStats | { data: AdminSupportStats }
    >('/admin/support/stats');
    return unwrap(response.data);
  }

  async getAdminTicket(uuid: string): Promise<AdminSupportTicketDetail> {
    const response = await this.api.get<
      AdminSupportTicketDetail | { data: AdminSupportTicketDetail }
    >(`/admin/support/tickets/${uuid}`);
    return unwrap(response.data);
  }

  async adminReply(
    uuid: string,
    content: string
  ): Promise<AdminSupportTicketDetail> {
    const response = await this.api.post<
      AdminSupportTicketDetail | { data: AdminSupportTicketDetail }
    >(`/admin/support/tickets/${uuid}/reply`, { content });
    return unwrap(response.data);
  }

  async retryAdminReplyEmail(
    ticketUuid: string,
    replyUuid: string
  ): Promise<AdminSupportTicketDetail> {
    const response = await this.api.post<
      AdminSupportTicketDetail | { data: AdminSupportTicketDetail }
    >(`/admin/support/tickets/${ticketUuid}/replies/${replyUuid}/retry-email`);
    return unwrap(response.data);
  }

  async closeAdminTicket(
    uuid: string,
    source: 'admin_confirmed' | 'external_email'
  ): Promise<AdminSupportTicketDetail> {
    const response = await this.api.post<
      AdminSupportTicketDetail | { data: AdminSupportTicketDetail }
    >(`/admin/support/tickets/${uuid}/close`, { source });
    return unwrap(response.data);
  }

  async updateAdminTicketStatus(
    uuid: string,
    status: SupportTicketStatus
  ): Promise<AdminSupportTicketDetail> {
    const response = await this.api.patch<
      AdminSupportTicketDetail | { data: AdminSupportTicketDetail }
    >(`/admin/support/tickets/${uuid}`, { status });
    return unwrap(response.data);
  }
}

export const supportService = new SupportService();
